import urlcat, { join } from 'urlcat';
import { HttpRequestOptions, IHttpClient } from './http.client.interface';

export default class BasicHttpClient implements IHttpClient {
  baseUrl: string;

  headers?: Record<string, string>;

  constructor() {
    this.baseUrl = '';

    this.handleError = this.handleError.bind(this);
    this.handleResponse = this.handleResponse.bind(this);
    this.makeRequest = this.makeRequest.bind(this);
    this.get = this.get.bind(this);
    this.post = this.post.bind(this);
    this.patch = this.patch.bind(this);
    this.delete = this.delete.bind(this);
    this.put = this.put.bind(this);
    this.getDefaultHeaders = this.getDefaultHeaders.bind(this);
    this.buildHeaders = this.buildHeaders.bind(this);
  }

  // This method might be overridden
  // eslint-disable-next-line class-methods-use-this
  getDefaultHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
    };
  }

  // Typically, this method would be overridden to handle errors
  // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
  handleError<Type>(
    error: unknown,
    _url: string,
    _config: HttpRequestOptions<Type>,
  ): Promise<Type> {
    throw error;
  }

  // This method might be overridden
  // eslint-disable-next-line class-methods-use-this
  async handleResponse<Type>(response: Response): Promise<Type> {
    return response.json();
  }

  private buildHeaders(headers?: HeadersInit): Record<string, string> {
    const givenHeaders: Record<string, string> = {};

    if (headers instanceof Headers) {
      headers.forEach((value, key) => {
        givenHeaders[key] = value;
      });
    } else if (Array.isArray(headers)) {
      headers.forEach(([key, value]) => {
        givenHeaders[key] = value;
      });
    } else if (typeof headers === 'object' && headers !== null) {
      Object.entries(headers).forEach(([key, value]) => {
        givenHeaders[key] = value;
      });
    }

    return { ...this.getDefaultHeaders(), ...this.headers, ...givenHeaders };
  }

  protected async makeRequest<Type>(url: string, options: HttpRequestOptions<Type>): Promise<Type> {
    try {
      const headers = this.buildHeaders(options.headers);
      if (options.body instanceof FormData) delete headers['Content-Type'];

      const requestOptions = { ...options, headers };
      if (options.method === 'GET' || options.method === 'HEAD') {
        delete requestOptions.body;
      }

      const response = await fetch(
        urlcat(join(this.baseUrl, '/', url.replace(this.baseUrl, '')), options.params ?? {}),
        requestOptions,
      );
      const onResponse = options.onResponse ?? this.handleResponse;

      if (!response.ok) throw await onResponse(response);

      return await onResponse(response);
    } catch (error) {
      return await (options.onError ?? this.handleError)(error, url, options);
    }
  }

  async get<Type>(url: string, options?: HttpRequestOptions<Type>): Promise<Type> {
    return this.makeRequest<Type>(url, {
      ...options,
      method: 'GET',
    });
  }

  async post<Type>(url: string, data?: object, options?: HttpRequestOptions<Type>): Promise<Type> {
    return this.makeRequest<Type>(url, {
      ...options,
      body: data instanceof FormData ? data : JSON.stringify(data),
      method: 'POST',
    });
  }

  async patch<Type>(url: string, data?: object, options?: HttpRequestOptions<Type>): Promise<Type> {
    return this.makeRequest<Type>(url, {
      ...options,
      body: data instanceof FormData ? data : JSON.stringify(data),
      method: 'PATCH',
    });
  }

  async delete<Type>(url: string, options?: HttpRequestOptions<Type>): Promise<Type> {
    return this.makeRequest<Type>(url, {
      ...options,
      method: 'DELETE',
    });
  }

  async put<Type>(url: string, data?: object, options?: HttpRequestOptions<Type>): Promise<Type> {
    return this.makeRequest<Type>(url, {
      ...options,
      body: data instanceof FormData ? data : JSON.stringify(data),
      method: 'PUT',
    });
  }
}
